import converter from 'json-2-csv'
import { export_json_to_excel } from '@/plugins/Export2Excel'
import { downloadFile } from '@/modules/Core/usings/generales'
import type { IConfReportes, IConfReportesParametros } from '@/modules/Reportes/interfaces'
import { reporteService } from '@/services'

interface ValidationError extends Error {
  fieldErrors: Record<string, string>
}

export const useReports = () => {

  /**
   * Función para parsear opciones de lista desde corpValor
   * Formato esperado: "Label1;Valor1|Label2;Valor2|Label3;Valor3"
   * @param corpValor - String con las opciones
   * @returns Array de opciones para SelectDefault
   */
  const parseListOptions = (corpValor: string | null) => {
    if (!corpValor) return []

    try {
      return corpValor
        .split('|')
        .map(option => {
          const [optionText, optionValue] = option.split(';')
          return {
            optionText: optionText?.trim() || '',
            optionValue: optionValue?.trim() || ''
          }
        })
        .filter(option => option.optionText && option.optionValue)
    } catch (error) {
      console.warn('Error parsing list options:', error)
      return []
    }
  }
  /**
   * Función que consulta y retorna los resultados para parámetros tipo LISTA_SQL
   * @param consulta - La consulta SQL a ejecutar
   * @param parametro - El parámetro al que se le asignarán las opciones
   * @returns Array con las opciones formateadas
   */
  const getConsultaResult = async (
    consulta: string,
    parametro: IConfReportesParametros
  ): Promise<{ optionText: string; optionValue: string }[]> => {
    try {
      const result = await reporteService.resultQuery(consulta)
      const {
        data: { body }
      } = result

      if (body && Array.isArray(body) && body.length > 0) {
        const firstRow = body[0]
        const keys = Object.keys(firstRow)

        let opciones: { optionText: string; optionValue: string }[] = []

        if (keys.length >= 2) {
          opciones = body.map(item => ({
            optionText: String(item[keys[0]]),
            optionValue: String(item[keys[1]])
          }))
        } else if (keys.length === 1) {
          opciones = body.map(item => ({
            optionText: String(item[keys[0]]),
            optionValue: String(item[keys[0]])
          }))
        }

        // También asignar a la propiedad lista del parámetro para compatibilidad
        parametro.lista = opciones

        return opciones
      } else {
        parametro.lista = []
        return []
      }
    } catch (error) {
      console.warn('Error executing SQL query for parameter:', error)
      parametro.lista = []
      return []
    }
  }

  /**
   * Inicializa los parámetros de un reporte
   * @param parametros - Array de parámetros del reporte
   */
  const initializeParameters = async (parametros: IConfReportesParametros[]) => {
    for (const parametro of parametros) {
      parametro.valor = null

      // Si es tipo LISTA, parsear las opciones desde corpValor
      if (parametro.tipo?.comaNombrecorto === 'LISTA' && parametro.corpValor) {
        parametro.lista = parseListOptions(parametro.corpValor)
      }

      // Si es tipo LISTA_SQL, ejecutar la consulta
      if (parametro.tipo?.comaNombrecorto === 'LISTA_SQL' && parametro.corpConsulta) {
        await getConsultaResult(parametro.corpConsulta, parametro)
      } else if (
        parametro.corpConsulta &&
        !parametro.corpValor &&
        parametro.tipo?.comaNombrecorto !== 'LISTA'
      ) {
        await getConsultaResult(parametro.corpConsulta, parametro)
      }
    }
  }

  /**
   * Ejecuta un reporte y retorna los datos
   * @param reporte - Configuración del reporte
   * @returns Datos del reporte ejecutado
   */
  const executeReport = async (reporte: IConfReportes) => {
    try {
      const result = await reporteService.execute({
        coreId: reporte.coreId,
        parametros: reporte.parametros
      })

      const {
        data: { body }
      } = result

      return body as Record<string, unknown>[]
    } catch (error) {
      console.error('Error executing report:', error)
      throw error
    }
  }

  /**
   * Descarga datos en formato CSV
   * @param data - Datos a descargar
   * @param filename - Nombre del archivo
   */
  const downloadCSV = async (data: Record<string, unknown>[], filename: string) => {
    try {
      const csv = await converter.json2csvAsync(data, {
        prependHeader: true,
        delimiter: {
          field: '|'
        }
      })
      const blob = new Blob([csv], {
        type: 'text/plain;charset=utf-8'
      })
      downloadFile(blob, `${filename}.txt`)
      window.notificacion.notificacion(
        'success',
        'Archivo CSV descargado correctamente'
      )
    } catch (error) {
      console.error('Error downloading CSV:', error)
      window.notificacion.notificacion('failure', 'Error al descargar el archivo CSV')
      throw error
    }
  }

  /**
   * Descarga datos en formato Excel
   * @param data - Datos a descargar
   * @param filename - Nombre del archivo
   */
  const downloadExcel = async (data: Record<string, unknown>[], filename: string) => {
    try {
      const head = [{ sheet: 'Page1', data: Object.keys(data[0]) }]
      const dataForExcel = [
        {
          sheet: 'Page1',
          data: data.map((item: Record<string, unknown>) => Object.values(item))
        }
      ]

      await export_json_to_excel({
        header: head,
        data: dataForExcel,
        filename: filename || 'reporte',
        autoWidth: true,
        bookType: 'xlsx',
        multiHeader: [],
        merges: [],
        dataValidations: [],
        sheets: ['Page1'],
        hiddens: [],
        download: true
      })

      window.notificacion.notificacion(
        'success',
        'Archivo Excel descargado correctamente'
      )
    } catch (error) {
      console.error('Error downloading Excel:', error)
      window.notificacion.notificacion('failure', 'Error al descargar el archivo Excel')
      throw error
    }
  }

  /**
   * Valida que todos los campos requeridos tengan valores
   * @param parametros - Array de parámetros del reporte
   * @returns Objeto con isValid (boolean), errores (array de strings) y fieldErrors (objeto con errores por campo)
   */
  const validateRequiredFields = (parametros: IConfReportesParametros[]) => {
    const errores: string[] = []
    const fieldErrors: Record<string, string> = {}

    for (const parametro of parametros) {
      if (parametro.corpIsRequired === 1) {
        const valor = parametro.valor

        // Verificar si el valor está vacío, null, undefined o es una cadena vacía
        if (valor === null || valor === undefined || valor === '' ||
            (typeof valor === 'string' && valor.trim() === '')) {
          const errorMessage = `El campo "${parametro.corpLabel}" es requerido`
          errores.push(errorMessage)
          fieldErrors[`param_${parametro.corpId}`] = errorMessage
        }
      }
    }

    return {
      isValid: errores.length === 0,
      errores,
      fieldErrors
    }
  }

  /**
   * Función principal para ejecutar reportes (consultar, CSV o Excel)
   * @param tipo - Tipo de ejecución: 'consultar', 'csv', 'excel'
   * @param reporte - Configuración del reporte
   * @param existingData - Datos existentes (opcional, para evitar re-ejecutar)
   * @returns Datos del reporte (si es consultar)
   */
  const ejecutarReporte = async (
    tipo: string,
    reporte: IConfReportes,
    existingData?: Record<string, unknown>[]
  ) => {
    try {
      // Validar campos requeridos antes de ejecutar
      if (reporte.parametros && reporte.parametros.length > 0) {
        const validacion = validateRequiredFields(reporte.parametros)

        if (!validacion.isValid) {
          // Mostrar todos los errores de validación
          for (const error of validacion.errores) {
            window.notificacion.notificacion('failure', error)
          }
          // Crear un error personalizado que incluya la información de los campos
          const validationError = new Error('Validación de campos requeridos fallida') as ValidationError
          validationError.fieldErrors = validacion.fieldErrors
          throw validationError
        }
      }

      let datosParaExportar = existingData

      if (
        tipo === 'consultar' ||
        !datosParaExportar ||
        datosParaExportar.length === 0
      ) {
        window.notificacion.notificacion('info', 'Ejecutando reporte...')
        datosParaExportar = await executeReport(reporte)
      } else {
        window.notificacion.notificacion(
          'info',
          `Generando archivo ${tipo.toUpperCase()}...`
        )
      }

      if (datosParaExportar && datosParaExportar.length > 0) {
        if (tipo === 'csv') {
          await downloadCSV(datosParaExportar, reporte.coreNombre || 'reporte')
        } else if (tipo === 'excel') {
          await downloadExcel(datosParaExportar, reporte.coreNombre || 'reporte')
        } else {
          // Retornar datos para mostrar en tabla
          window.notificacion.notificacion(
            'success',
            `Se encontraron ${datosParaExportar.length} registros`
          )
          return datosParaExportar
        }
      } else {
        window.notificacion.notificacion(
          'warning',
          'No se encontraron resultados para los parámetros especificados'
        )
        return []
      }
    } catch (error) {
      console.error('Error en ejecutarReporte:', error)
      window.notificacion.notificacion('failure', 'Error al ejecutar el reporte')
      throw error
    }
  }
  /**
   * Ejecuta una vista previa de reporte sin necesidad de coreId
   * @param consultaSQL - La consulta SQL del reporte
   * @param parametros - Array de parámetros del reporte
   * @returns Datos del reporte ejecutado
   */
  const executePreview = async (consultaSQL: string, parametros: IConfReportesParametros[]) => {
    try {
      // Validar campos requeridos antes de ejecutar la vista previa
      if (parametros && parametros.length > 0) {
        const validacion = validateRequiredFields(parametros)

        if (!validacion.isValid) {
          // Mostrar todos los errores de validación
          for (const error of validacion.errores) {
            window.notificacion.notificacion('failure', error)
          }
          throw new Error('Validación de campos requeridos fallida')
        }
      }

      const result = await reporteService.executePreview({
        consulta: consultaSQL,
        parametros: parametros
      })

      const {
        data: { body }
      } = result

      return body as Record<string, unknown>[]
    } catch (error) {
      console.error('Error executing preview:', error)
      throw error
    }
  }

  return {
    parseListOptions,
    getConsultaResult,
    initializeParameters,
    executeReport,
    downloadCSV,
    downloadExcel,
    ejecutarReporte,
    executePreview,
    validateRequiredFields
  }
}
